'use client'

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

const COUNTRY_STORAGE_KEY = 'pricing_country'
const RATE_CACHE_KEY = 'pricing_rate_cache_v1'
const RATE_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const DEFAULT_EXCHANGE_RATE_API_KEY = 'b74f2dd77179fb9eb3e839d8'
const DEFAULT_COUNTRY_CODE: SupportedPricingCountryCode = 'DE'

export type SupportedPricingCountryCode =
  | 'US'
  | 'GB'
  | 'IN'
  | 'CH'
  | 'AU'
  | 'CA'
  | 'SG'
  | 'AE'
  | 'JP'
  | 'KR'
  | 'DE'
  | 'FR'
  | 'ES'
  | 'IT'
  | 'NL'
  | 'BE'
  | 'AT'
  | 'IE'

export type PricingCountryOption = {
  code: SupportedPricingCountryCode
  label: string
  currency: string
}

type ExchangeRateCache = {
  expiresAt: number
  rates: Record<string, number>
}

type PricingLocalizationContextValue = {
  countryCode: string | null
  detectedCountryCode: string | null
  manualCountryCode: SupportedPricingCountryCode | null
  displayCurrency: string
  displayLocale: string
  conversionRate: number
  loading: boolean
  fallbackToEuro: boolean
  error: string | null
  setManualCountry: (countryCode: SupportedPricingCountryCode) => void
  clearManualCountry: () => void
}

export type LocalizedPricingResult = PricingLocalizationContextValue

export const SUPPORTED_PRICING_COUNTRIES: PricingCountryOption[] = [
  { code: 'US', label: 'United States', currency: 'USD' },
  { code: 'GB', label: 'United Kingdom', currency: 'GBP' },
  { code: 'IN', label: 'India', currency: 'INR' },
  { code: 'CH', label: 'Switzerland', currency: 'CHF' },
  { code: 'AU', label: 'Australia', currency: 'AUD' },
  { code: 'CA', label: 'Canada', currency: 'CAD' },
  { code: 'SG', label: 'Singapore', currency: 'SGD' },
  { code: 'AE', label: 'United Arab Emirates', currency: 'AED' },
  { code: 'JP', label: 'Japan', currency: 'JPY' },
  { code: 'KR', label: 'South Korea', currency: 'KRW' },
  { code: 'DE', label: 'Germany', currency: 'EUR' },
  { code: 'FR', label: 'France', currency: 'EUR' },
  { code: 'ES', label: 'Spain', currency: 'EUR' },
  { code: 'IT', label: 'Italy', currency: 'EUR' },
  { code: 'NL', label: 'Netherlands', currency: 'EUR' },
  { code: 'BE', label: 'Belgium', currency: 'EUR' },
  { code: 'AT', label: 'Austria', currency: 'EUR' },
  { code: 'IE', label: 'Ireland', currency: 'EUR' },
]

const SUPPORTED_COUNTRY_MAP = new Map(
  SUPPORTED_PRICING_COUNTRIES.map((country) => [country.code, country]),
)

const PricingLocalizationContext = createContext<PricingLocalizationContextValue | undefined>(undefined)

function isClient(): boolean {
  return typeof window !== 'undefined'
}

function getBrowserLocale(): string {
  if (!isClient()) return 'en-US'
  return navigator.language || navigator.languages?.[0] || 'en-US'
}

function getStoredCountry(): SupportedPricingCountryCode | null {
  if (!isClient()) return null
  const value = window.localStorage.getItem(COUNTRY_STORAGE_KEY)
  if (!value) return null
  return SUPPORTED_COUNTRY_MAP.has(value as SupportedPricingCountryCode)
    ? (value as SupportedPricingCountryCode)
    : null
}

function readRateCache(): ExchangeRateCache | null {
  if (!isClient()) return null

  try {
    const raw = window.localStorage.getItem(RATE_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ExchangeRateCache
    if (!parsed.expiresAt || parsed.expiresAt <= Date.now()) return null
    return parsed
  } catch {
    return null
  }
}

function writeRateCache(rates: Record<string, number>) {
  if (!isClient()) return

  try {
    const payload: ExchangeRateCache = {
      expiresAt: Date.now() + RATE_CACHE_TTL_MS,
      rates,
    }
    window.localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore storage failures and keep the in-memory value only.
  }
}

function getCountryCurrency(countryCode: string | null): string {
  if (!countryCode) return 'EUR'
  return SUPPORTED_COUNTRY_MAP.get(countryCode as SupportedPricingCountryCode)?.currency || 'EUR'
}

function getCountryLabel(countryCode: string | null): string {
  if (!countryCode) return 'Auto'
  return SUPPORTED_COUNTRY_MAP.get(countryCode as SupportedPricingCountryCode)?.label || countryCode
}

async function fetchExchangeRates(signal: AbortSignal): Promise<Record<string, number>> {
  const apiKey =
    import.meta.env.VITE_EXCHANGERATE_API_KEY?.trim() ||
    DEFAULT_EXCHANGE_RATE_API_KEY
  if (!apiKey) {
    throw new Error('Missing ExchangeRate API key')
  }

  const response = await fetch(
    `https://v6.exchangerate-api.com/v6/${apiKey}/latest/EUR`,
    { signal },
  )

  if (!response.ok) {
    throw new Error(`Exchange rate request failed (${response.status})`)
  }

  const body = (await response.json()) as {
    result?: string
    conversion_rates?: Record<string, number>
    ['error-type']?: string
  }

  if (body.result !== 'success' || !body.conversion_rates) {
    throw new Error(body['error-type'] || 'Exchange rate request failed')
  }

  return body.conversion_rates
}

function getCachedRate(currency: string): number | null {
  if (currency === 'EUR') return 1
  const cache = readRateCache()
  const rate = cache?.rates?.[currency]
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : null
}

async function getExchangeRate(currency: string, signal: AbortSignal): Promise<number> {
  if (currency === 'EUR') return 1

  const cachedRate = getCachedRate(currency)
  if (cachedRate) return cachedRate

  const rates = await fetchExchangeRates(signal)
  writeRateCache(rates)

  const rate = rates[currency]
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
    throw new Error(`Missing conversion rate for ${currency}`)
  }

  return rate
}

export function PricingLocalizationProvider({ children }: { children: ReactNode }) {
  const storedCountry = getStoredCountry()
  const initialCountryCode = storedCountry ?? DEFAULT_COUNTRY_CODE
  const initialCurrency = getCountryCurrency(initialCountryCode)
  const [manualCountryCode, setManualCountryCode] = useState<SupportedPricingCountryCode | null>(
    () => storedCountry,
  )
  const [detectedCountryCode, setDetectedCountryCode] = useState<string | null>(
    () => initialCountryCode,
  )
  const [displayCurrency, setDisplayCurrency] = useState<string>(() =>
    initialCurrency,
  )
  const [conversionRate, setConversionRate] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [fallbackToEuro, setFallbackToEuro] = useState<boolean>(initialCurrency === 'EUR')
  const [error, setError] = useState<string | null>(null)

  const displayLocale = getBrowserLocale()
  const countryCode = manualCountryCode ?? detectedCountryCode ?? DEFAULT_COUNTRY_CODE

  useEffect(() => {
    if (!isClient()) return undefined

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== COUNTRY_STORAGE_KEY) return
      const nextStoredCountry = getStoredCountry()
      const nextCountryCode = nextStoredCountry ?? DEFAULT_COUNTRY_CODE
      setError(null)
      setManualCountryCode(nextStoredCountry)
      setDetectedCountryCode(nextCountryCode)
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(() => {
    if (!isClient()) return undefined

    const controller = new AbortController()
    let cancelled = false

    async function synchronizePricing() {
      const resolvedCountryCode = manualCountryCode ?? DEFAULT_COUNTRY_CODE
      const nextCurrency = getCountryCurrency(resolvedCountryCode)
      setDisplayCurrency(nextCurrency)
      setDetectedCountryCode((current) => (manualCountryCode ? current : resolvedCountryCode))
      setError(null)

      if (nextCurrency === 'EUR') {
        setDisplayCurrency(nextCurrency)
        setConversionRate(1)
        setFallbackToEuro(true)
        setLoading(false)
        return
      }

      const cachedRate = getCachedRate(nextCurrency)
      if (cachedRate) {
        setConversionRate(cachedRate)
        setFallbackToEuro(false)
        setLoading(false)
        return
      }

      setLoading(true)

      try {
        const nextRate = await getExchangeRate(nextCurrency, controller.signal)
        if (cancelled || controller.signal.aborted) return

        setDisplayCurrency(nextCurrency)
        setConversionRate(nextRate)
        setFallbackToEuro(false)
        setLoading(false)
        setError(null)
      } catch (rateError) {
        if (cancelled || controller.signal.aborted) return

        setDisplayCurrency('EUR')
        setConversionRate(1)
        setFallbackToEuro(true)
        setLoading(false)
        setError(rateError instanceof Error ? rateError.message : 'Failed to load exchange rates')
      }
    }

    synchronizePricing()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [manualCountryCode])

  const value = useMemo<PricingLocalizationContextValue>(
    () => ({
      countryCode,
      detectedCountryCode,
      manualCountryCode,
      displayCurrency,
      displayLocale,
      conversionRate,
      loading,
      fallbackToEuro,
      error,
      setManualCountry: (countryCode) => {
        if (!isClient()) return
        const nextCurrency = getCountryCurrency(countryCode)
        window.localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode)
        setDisplayCurrency(nextCurrency)
        setConversionRate(nextCurrency === 'EUR' ? 1 : conversionRate)
        setFallbackToEuro(nextCurrency === 'EUR')
        setLoading(nextCurrency !== 'EUR' && !getCachedRate(nextCurrency))
        setError(null)
        setManualCountryCode(countryCode)
      },
      clearManualCountry: () => {
        if (!isClient()) return
        window.localStorage.removeItem(COUNTRY_STORAGE_KEY)
        setDisplayCurrency('EUR')
        setConversionRate(1)
        setFallbackToEuro(true)
        setLoading(false)
        setError(null)
        setManualCountryCode(null)
        setDetectedCountryCode(DEFAULT_COUNTRY_CODE)
      },
    }),
    [
      conversionRate,
      countryCode,
      detectedCountryCode,
      displayCurrency,
      displayLocale,
      error,
      fallbackToEuro,
      loading,
      manualCountryCode,
    ],
  )

  return createElement(PricingLocalizationContext.Provider, { value }, children)
}

export function useLocalizedPricing(): LocalizedPricingResult {
  const context = useContext(PricingLocalizationContext)
  if (!context) {
    throw new Error('useLocalizedPricing must be used within PricingLocalizationProvider')
  }
  return context
}

export function getPricingCountryMeta(countryCode: string | null): {
  code: string | null
  label: string
  currency: string
} {
  return {
    code: countryCode,
    label: getCountryLabel(countryCode),
    currency: getCountryCurrency(countryCode),
  }
}
